/**
 * Entry point — registers `<minirag-widget>` and, for the zero-JS embed
 * path, auto-mounts one from the loading `<script>` tag's `data-*`
 * attributes. CSS is imported with `?inline` (a raw string, not a stylesheet
 * Vite emits separately) so the whole widget ships as the one file a
 * `<script src>` embed can load in a single request — see vite.config.ts.
 */
import { h, render } from 'preact';

import { fetchWidgetTheme } from './api';
import { Widget, type WidgetProps } from './Widget';
import widgetCss from './widget.css?inline';

type Position = 'bottom-right' | 'bottom-left';

function resolvePosition(value: string | null | undefined): Position {
  return value === 'bottom-left' ? 'bottom-left' : 'bottom-right';
}

class MiniRAGWidgetElement extends HTMLElement {
  private mounted = false;

  connectedCallback(): void {
    if (this.mounted) return;
    this.mounted = true;

    const siteKey = this.getAttribute('site-key') ?? '';
    if (!siteKey) {
      // Fails loudly rather than silently no-op'ing — a missing site key is
      // always a configuration mistake worth surfacing to whoever embedded
      // this, not a state the widget should quietly render nothing for.
      console.error('[minirag-widget] missing required "site-key" attribute — chat will not load.');
      return;
    }

    const apiUrl = this.getAttribute('api-url') || window.location.origin;

    // An explicit data-* attribute is a deliberate per-embed override and
    // always wins. Anything left unset falls back to the theme configured
    // from the dashboard's Customize dialog (fetched below), then to the
    // widget's own hardcoded defaults, in that order.
    const explicitTitle = this.getAttribute('title');
    const explicitPosition = this.getAttribute('position');
    const explicitColor = this.getAttribute('primary-color');

    const props: WidgetProps = {
      apiUrl,
      siteKey,
      title: explicitTitle || 'Chat with us',
      position: resolvePosition(explicitPosition),
    };

    // A custom property set on the host element inherits into the shadow
    // tree, so theming a color needs no re-render — just this one line.
    if (explicitColor) this.style.setProperty('--mrw-primary', explicitColor);

    const shadow = this.attachShadow({ mode: 'open' });

    const style = document.createElement('style');
    style.textContent = widgetCss;
    shadow.appendChild(style);

    const mountPoint = document.createElement('div');
    shadow.appendChild(mountPoint);

    render(h(Widget, props), mountPoint);

    // Fetched eagerly, not lazily on first click like the session token —
    // the closed bubble is the first thing a visitor sees, so it should
    // already be the tenant's chosen color rather than flashing the default
    // until they open it. The initial render above already painted with
    // safe defaults, so this only upgrades it once the network responds;
    // re-rendering into the same mount point patches the existing Preact
    // tree in place rather than remounting it, so no in-progress chat state
    // is lost if this resolves after the visitor has already started typing.
    void fetchWidgetTheme(apiUrl, siteKey).then((theme) => {
      if (!theme) return;

      if (!explicitColor && theme.primaryColor) {
        this.style.setProperty('--mrw-primary', theme.primaryColor);
      }

      // Per-surface colour overrides. Each is only set when the tenant
      // actually picked one; anything left null keeps resolving through the
      // var() fallback chain in widget.css (→ --mrw-primary or a neutral).
      const cssVars: Record<string, string | null> = {
        '--mrw-header': theme.headerColor,
        '--mrw-launcher': theme.launcherColor,
        '--mrw-send': theme.sendButtonColor,
        '--mrw-user-bg': theme.userBubbleColor,
        '--mrw-user-text': theme.userTextColor,
        '--mrw-bot-bg': theme.botBubbleColor,
        '--mrw-bot-text': theme.botTextColor,
        '--mrw-window-bg': theme.windowBgColor,
      };
      for (const [name, value] of Object.entries(cssVars)) {
        if (value) this.style.setProperty(name, value);
      }

      // Launcher geometry — diameter preset + gap from the screen edges.
      const sizePx = { small: '48px', medium: '56px', large: '68px' }[theme.launcherSize ?? ''];
      if (sizePx) this.style.setProperty('--mrw-launcher-size', sizePx);
      if (theme.launcherOffsetBottom != null) {
        this.style.setProperty('--mrw-offset-bottom', `${theme.launcherOffsetBottom}px`);
      }
      if (theme.launcherOffsetSide != null) {
        this.style.setProperty('--mrw-offset-side', `${theme.launcherOffsetSide}px`);
      }

      render(
        h(Widget, {
          ...props,
          title: explicitTitle || theme.title || props.title,
          position: explicitPosition ? props.position : (theme.position ?? props.position),
          suggestedQuestions: theme.suggestedQuestions,
          iconUrl: theme.iconUrl ?? undefined,
          inputPlaceholder: theme.inputPlaceholder ?? undefined,
          launcherShadow: theme.launcherShadow ?? undefined,
          launcherMotion: theme.launcherMotion ?? undefined,
        }),
        mountPoint,
      );
    });
  }
}

if (!customElements.get('minirag-widget')) {
  customElements.define('minirag-widget', MiniRAGWidgetElement);
}

// Zero-JS embed: <script src=".../widget.js" data-site-key="pk_live_...">.
// Mirrors the old widget's script-tag config, minus `data-api-token` — that
// tenant-wide credential in public HTML is exactly what this rewrite removes.
const scriptEl = document.currentScript as HTMLScriptElement | null;
if (scriptEl?.dataset.siteKey) {
  const el = document.createElement('minirag-widget');
  el.setAttribute('site-key', scriptEl.dataset.siteKey);
  if (scriptEl.dataset.apiUrl) el.setAttribute('api-url', scriptEl.dataset.apiUrl);
  if (scriptEl.dataset.title) el.setAttribute('title', scriptEl.dataset.title);
  if (scriptEl.dataset.position) el.setAttribute('position', scriptEl.dataset.position);
  if (scriptEl.dataset.primaryColor)
    el.setAttribute('primary-color', scriptEl.dataset.primaryColor);
  document.body.appendChild(el);
}
